'use client';
import React from 'react';
import { Button } from '../button';
import { Collapsible } from '../collapsible';
import { cn } from '../../lib/utils';

const tones = {
  info: 'border-status-information-border bg-status-information-bg text-status-information-fg',
  critical: 'border-status-critical-border bg-status-critical-bg text-status-critical-fg',
  information:
    'border-status-information-border bg-status-information-bg text-status-information-fg',
  warning: 'border-status-warning-border bg-status-warning-bg text-status-warning-fg',
  danger: 'border-status-danger-border bg-status-danger-bg text-status-danger-fg',
  success: 'border-status-success-border bg-status-success-bg text-status-success-fg',
  neutral: 'border-status-neutral-border bg-status-neutral-bg text-status-neutral-fg',
} as const;

/** Presentation only: callers retain announcement, dismissal and expansion logic. */
export function Banner({
  tone = 'neutral',
  className,
  children,
  details,
  actions,
  onDismiss,
  dismissLabel = 'Dismiss notice',
  ...props
}: React.ComponentPropsWithoutRef<'div'> & {
  tone?: keyof typeof tones;
  details?: React.ReactNode;
  /** Supply existing Button or link-button controls; handlers stay with the caller. */
  actions?: React.ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
}) {
  return (
    <div
      {...props}
      data-banner
      className={cn(
        'min-w-0 break-words rounded-control border px-4 py-3 font-body text-body',
        tones[tone],
        className,
      )}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1">{children}</div>
        {onDismiss && (
          <Button type="button" variant="tertiary" onClick={onDismiss} aria-label={dismissLabel}>
            Dismiss
          </Button>
        )}
      </div>
      {details != null && (
        <Collapsible label="Details" className="mt-2 bg-transparent" triggerClassName="text-body">
          {details}
        </Collapsible>
      )}
      {actions != null && (
        <div data-banner-actions className="mt-2 flex min-w-0 flex-wrap items-center gap-2">
          {actions}
        </div>
      )}
    </div>
  );
}

/** One ordered stack; notice owners keep their existing render/visibility decisions. */
export function BannerSlot({
  environment,
  tenant,
  record,
  className,
}: {
  environment?: React.ReactNode;
  tenant?: React.ReactNode;
  record?: React.ReactNode;
  className?: string;
}) {
  if (!environment && !tenant && !record) return null;
  return (
    <div data-banner-slot className={cn('flex min-w-0 flex-col gap-2', className)}>
      {environment && <Banner data-banner-priority="environment">{environment}</Banner>}
      {tenant && <Banner data-banner-priority="tenant">{tenant}</Banner>}
      {record && <Banner data-banner-priority="record">{record}</Banner>}
    </div>
  );
}

/** Compatibility notice card: all notice anatomy and actions are owned by Banner. */
export function InfoCard(props: React.ComponentProps<typeof Banner>) {
  return <Banner tone="information" {...props} />;
}
